/**
 * Device Information Utility
 *
 * Generates device fingerprint and information for session tracking
 */

/**
 * Generate a simple device ID based on browser characteristics
 * Note: For production, consider using a fingerprinting library like FingerprintJS
 */
export function generateDeviceId() {
    // Check for existing device ID in localStorage
    const storedId = typeof window !== 'undefined' ? localStorage.getItem('sa_device_id') : null;
    if (storedId) {
        return storedId;
    }

    // Generate new device ID
    const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset(),
        screen.width,
        screen.height,
        screen.colorDepth
    ];

    // Simple hash of components
    const hash = components.join('|').split('').reduce((acc, char) => {
        const hash = ((acc << 5) - acc) + char.charCodeAt(0);
        return hash & hash;
    }, 0);

    const deviceId = `dev_${Math.abs(hash).toString(36)}_${Date.now().toString(36)}`;

    // Store for future use
    if (typeof window !== 'undefined') {
        localStorage.setItem('sa_device_id', deviceId);
    }

    return deviceId;
}

/**
 * Parse browser name from user agent
 */
export function parseBrowser(userAgent) {
    if (!userAgent) return 'Unknown';

    if (userAgent.includes('Edg/')) return 'Edge';
    if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'Internet Explorer';

    return 'Unknown';
}

/**
 * Parse OS from user agent
 */
export function parseOS(userAgent) {
    if (!userAgent) return 'Unknown';

    if (userAgent.includes('Windows NT 10')) return 'Windows 10';
    if (userAgent.includes('Windows NT 6.3')) return 'Windows 8.1';
    if (userAgent.includes('Windows NT 6.2')) return 'Windows 8';
    if (userAgent.includes('Windows NT 6.1')) return 'Windows 7';
    if (userAgent.includes('Windows')) return 'Windows';

    if (userAgent.includes('Mac OS X')) {
        const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
        return match ? `macOS ${match[1].replace('_', '.')}` : 'macOS';
    }

    if (userAgent.includes('Android')) {
        const match = userAgent.match(/Android (\d+\.?\d*)/);
        return match ? `Android ${match[1]}` : 'Android';
    }

    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
        const match = userAgent.match(/OS (\d+[._]\d+)/);
        return match ? `iOS ${match[1].replace('_', '.')}` : 'iOS';
    }

    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('CrOS')) return 'Chrome OS';

    return 'Unknown';
}

/**
 * Parse device type from user agent
 */
export function parseDeviceType(userAgent) {
    if (!userAgent) return 'UNKNOWN';

    if (/Mobi|Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
        return 'MOBILE';
    }

    if (/iPad|Android(?!.*Mobile)|Tablet/i.test(userAgent)) {
        return 'TABLET';
    }

    return 'DESKTOP';
}

/**
 * Get complete device information
 */
export function getDeviceInfo() {
    if (typeof window === 'undefined') {
        return {
            deviceId: 'server',
            deviceType: 'UNKNOWN',
            browser: 'Unknown',
            os: 'Unknown',
            userAgent: ''
        };
    }

    const userAgent = navigator.userAgent;

    return {
        deviceId: generateDeviceId(),
        deviceName: `${parseBrowser(userAgent)} on ${parseOS(userAgent)}`,
        deviceType: parseDeviceType(userAgent),
        browser: parseBrowser(userAgent),
        os: parseOS(userAgent),
        userAgent: userAgent,
        // Location info would come from IP geolocation on the backend
        // or could be fetched from a geolocation API
        ipAddress: null,
        location: null,
        city: null,
        countryCode: null,
        country: null
    };
}

export default {
    generateDeviceId,
    parseBrowser,
    parseOS,
    parseDeviceType,
    getDeviceInfo
};
